// @noflow
import { getNameForProduct, getProductCapabilities, makeGetProductItemById } from '~/core/state/products/selectors';
import {
    handleNotificationChange,
    handlePropertyChange,
    selectConsumer,
    selectProducer
} from '~/core/state/recipes/actions';
import { isConsumerActive, templates } from '~/core/utils/recipes';
import { makeGetConsumersList, makeGetProducersList } from '~/core/state/recipes/selectors';
import { notificationTypes, recipeStatus } from '~/core/constants/recipes';

import { connect } from 'react-redux';
import { deviceTypes } from '~/core/constants/devices';
import { features } from '~/core/constants/subscriptions';
import { fromJS } from 'immutable';
import { getConfig } from '~/core/state/config/selectors';
import { hasSMS } from '~/core/state/entitlements/selectors';
import PropTypes from 'prop-types';
import RecipeDetail from './component';
import { showHiveLiveModal } from '~/core/state/application/actions';

const mapStateToProps = state => {
    const getProducersList = makeGetProducersList();
    const getConsumersList = makeGetConsumersList();
    const getProductItemById = makeGetProductItemById();
    const notificationsConfig = getConfig(state, 'recipes:notifications');

    return (state, { currentRecipe, specificConsumerId, specificProducerId }) => {
        const templateName = currentRecipe.get('name');
        const status = currentRecipe.get('status');
        let producerIndex, producerName, producerTrigger, producerType, producers;
        const producerId = currentRecipe.getIn(['data', 'producer']);
        if ([recipeStatus.EDITING, recipeStatus.UPDATING].includes(status)) {
            const selectedProduct = getProductItemById(state, {
                id: producerId
            });
            producerName = selectedProduct.getIn(['state', 'name']);
            producerTrigger = currentRecipe.getIn(['data', 'trigger', 'eventName']);
            producerType = selectedProduct.get('type');
            producers = fromJS([
                {
                    id: producerId,
                    name: getNameForProduct(state, { id: producerId })
                }
            ]);
            producerIndex = 0;
        } else {
            producerType = templates[templateName].producer.type;
            producers = getProducersList(state, { name: templateName });
            producerIndex = producers.indexOf(producers.find(p => p.get('id') === producerId));
        }
        if (specificProducerId) {
            // For recipes shown in a producer product page we only want to deal with this producer
            producers = producers.filter(producer => producer.get('id') === specificProducerId);
            producerIndex = 0;
        }

        const isNotification = currentRecipe.get('isNotification');
        let props = {
            isActive: currentRecipe.get('isActive'),
            isNotification,
            producerName,
            producerTrigger,
            producerType,
            producers,
            producerIndex,
            templateName
        };
        if (isNotification) {
            props.notificationOptions = currentRecipe.getIn(['data', 'action', 'value']);
            // Remove the options that are not supported in the user country
            notificationsConfig.entrySeq().forEach(([key, value]) => {
                if (!value) {
                    props.notificationOptions = props.notificationOptions.delete(key);
                }
            });
            // check that the user has the entitlement for sms
            props.hasSMSEntitlement = hasSMS(state);
            if (!props.hasSMSEntitlement && props.notificationOptions.get(notificationTypes.sms)) {
                props.notificationOptions = props.notificationOptions.set(notificationTypes.sms, false);
            }
        } else {
            let consumerDuration, consumerIndex, consumerName, consumerType, consumers;
            const consumerId = currentRecipe.getIn(['data', 'consumer']);
            if ([recipeStatus.EDITING, recipeStatus.UPDATING].includes(status)) {
                const selectedConsumer = getProductItemById(state, {
                    id: consumerId
                });
                consumerDuration = currentRecipe.getIn(['data', 'action', 'duration']);
                consumerName = selectedConsumer.getIn(['state', 'name']);
                consumerType = selectedConsumer.get('type');
                consumers = fromJS([{ id: consumerId, name: consumerName }]);
                consumerIndex = 0;
            } else {
                consumerType = templates[templateName].consumer.types[0];
                consumers = getConsumersList(state, { name: templateName });
                consumerIndex = consumers.indexOf(consumers.find(p => p.get('id') === consumerId));
                if (specificConsumerId) {
                    // For recipes shown in a consumer product page we only want to deal with this consumer
                    consumers = consumers.filter(consumer => consumer.get('id') === specificConsumerId);
                    consumerIndex = 0;
                }
            }
            let consumerPropertiesList = fromJS([]);
            if (consumerId !== -1) {
                consumerPropertiesList = currentRecipe.get('consumerPropertiesList', fromJS([]));
                // if it is heating then check if device has the boost capability
                if (
                    consumerType === deviceTypes.HEATING &&
                    !getProductCapabilities(state, { id: consumerId }).includes('BOOST')
                ) {
                    // if not remove the boost option from the mode property
                    const modePropertyIndex = consumerPropertiesList.findIndex(p => p.get('field') === 'mode');
                    consumerPropertiesList = consumerPropertiesList.removeIn([modePropertyIndex, 'optionsList', 3]);
                }
            }
            props = {
                ...props,
                consumerDuration,
                consumerName,
                consumerIndex,
                consumerPropertiesList,
                consumerState: currentRecipe.getIn(['data', 'action', 'changes']),
                consumerType,
                consumers,
                isConsumerActive: isConsumerActive(currentRecipe, consumerType)
            };
        }
        return props;
    };
};

const mapDispatchToProps = {
    handleNotificationChange,
    handlePropertyChange,
    selectConsumer,
    selectProducer,
    showHiveLiveModal
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const {
        consumers,
        consumerPropertiesList,
        consumerType,
        hasSMSEntitlement,
        isNotification,
        producers
    } = stateProps;
    const {
        handleNotificationChange,
        handlePropertyChange,
        selectConsumer,
        selectProducer,
        showHiveLiveModal
    } = dispatchProps;
    const { specificConsumerId } = ownProps;

    return {
        ...stateProps,
        ...ownProps,
        consumerPropertiesList:
            !isNotification &&
            consumerPropertiesList.map(o =>
                o.set('handler', index =>
                    handlePropertyChange({
                        index,
                        property: o.get('type'),
                        consumerType
                    })
                )
            ),
        consumersList: !isNotification && consumers.map(p => p.get('name')),
        handleNotificationChange: notificationType => {
            return notificationType === notificationTypes.sms && !hasSMSEntitlement
                ? showHiveLiveModal({ feature: features.textNotifications })
                : handleNotificationChange(notificationType);
        },
        producersList: producers.map(p => p.get('name')),
        selectConsumer: index => !isNotification && selectConsumer(consumers.getIn([index, 'id'])),
        selectProducer: index =>
            selectProducer({
                producerId: producers.getIn([index, 'id']),
                specificConsumerId
            })
    };
};

const ConnectedRecipeDetail = connect(mapStateToProps, mapDispatchToProps, mergeProps)(RecipeDetail);

ConnectedRecipeDetail.propTypes = {
    currentRecipe: PropTypes.object.isRequired
};

export default ConnectedRecipeDetail;



// WEBPACK FOOTER //
// ./src/components/widgets/recipes/recipe-detail/index.js